import {
    useContext,
    useState
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    Image,
    View
} from "react-native";
import type SuperAdminFormModalProps from "./type";
import type {
    SuperAdmin
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitModal,
    NCoreUIKitTheme,
    SelectBox,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    launchImageLibrary
} from "react-native-image-picker";
import {
    UserCogIcon,
    PencilIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../../../../../contexts/AuthContext";

const SuperAdminFormModal = ({
    organizations,
    initialData,
    onSuccess,
    modalId
}: SuperAdminFormModalProps) => {
    const isEdit = !!initialData;
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        updateUser,
        user
    } = useContext(AuthContext);

    const [
        formData,
        setFormData
    ] = useState<Partial<SuperAdmin> & { password?: string }>({
        organization_ids: Array.isArray(initialData?.organization_ids) && initialData.organization_ids.length > 0
            ? initialData.organization_ids
            : (initialData?.organization_id ? [initialData.organization_id] : []),
        profilePicturePath: initialData?.profilePicturePath || "",
        adminNote: initialData?.adminNote || "",
        fullName: initialData?.fullName || "",
        username: initialData?.username || "",
        address: initialData?.address || "",
        about: initialData?.about || "",
        email: initialData?.email || "",
        phone: initialData?.phone || "",
        password: ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const handleChange = (key: keyof SuperAdmin | "password", value: string | string[] | null) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleImagePick = async () => {
        const result = await launchImageLibrary({
            includeBase64: true,
            mediaType: "photo",
            quality: 0.5
        });

        if (!result.didCancel && result.assets && result.assets.length > 0) {
            const asset = result.assets[0];
            if (asset) {
                const base64Image = `data:${asset.type || "image/jpeg"};base64,${asset.base64}`;
                handleChange("profilePicturePath", base64Image);
            }
        }
    };

    const handleSubmit = async () => {
        if (!formData.username || formData.username.length < 2) {
            NCoreUIKitToast.open({
                title: localize("superadmin-form-alert-username-length")
            });
            return;
        }
        if (!formData.email) {
            NCoreUIKitToast.open({
                title: localize("superadmin-form-alert-email-required")
            });
            return;
        }
        if (!isEdit && (!formData.password || formData.password.length < 6)) {
            NCoreUIKitToast.open({
                title: localize("superadmin-form-alert-password-length")
            });
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = isEdit
                ? `${process.env.API_URL}/users/${initialData?.id}`
                : `${process.env.API_URL}/users/super-admin`;

            const method = isEdit ? "PUT" : "POST";

            const response = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(formData),
                method
            });

            const data = await response.json();
            if (data.status === "success") {
                NCoreUIKitToast.open({
                    title: data.message || localize("superadmin-form-toast-success")
                });
                if (initialData?.id === user?.id) {
                    await updateUser({
                        profilePicturePath: formData.profilePicturePath,
                        fullName: formData.fullName,
                        username: formData.username,
                        email: formData.email
                    });
                }
                onSuccess();
                NCoreUIKitModal.close({
                    id: modalId
                });
            } else {
                NCoreUIKitToast.open({
                    title: data.message || localize("superadmin-form-toast-error")
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("superadmin-form-toast-server-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <View
        style={[
            stylesheet.container,
            {
                backgroundColor: colors.content.container.default,
                width: isMobile ? "100%" : 500,
                borderRadius: radiuses.md,
                padding: spaces.spacingXl
            }
        ]}
    >
        <Text
            style={stylesheet.unselectableText}
            variant="titleMediumSize"
        >
            {isEdit ? localize("superadmin-form-title-edit") : localize("superadmin-form-title-create")}
        </Text>
        <ScrollView
            contentContainerStyle={[
                stylesheet.formContainer,
                {
                    paddingBottom: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
            showsVerticalScrollIndicator={false}
            style={[
                stylesheet.scroll,
                {
                    marginTop: spaces.spacingXl,
                    flexShrink: 1
                }
            ]}
        >
            <View
                style={[
                    stylesheet.imagePickerContainer,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <TouchableOpacity
                    style={stylesheet.imagePickerTouchable}
                    onPress={handleImagePick}
                    activeOpacity={0.8}
                >
                    {
                        formData.profilePicturePath ?
                            <Image
                                source={{
                                    uri: formData.profilePicturePath
                                }}
                                style={[
                                    stylesheet.profileImage,
                                    {
                                        borderRadius: radiuses.full
                                    }
                                ]}
                            />
                        :
                            <View
                                style={[
                                    stylesheet.profileImage,
                                    {
                                        backgroundColor: colors.content.container.mid,
                                        borderRadius: radiuses.full,
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }
                                ]}
                            >
                                <UserCogIcon
                                    color={colors.content.icon.high}
                                    size={40}
                                />
                            </View>
                    }
                    <View
                        style={[
                            stylesheet.editIconContainer,
                            {
                                backgroundColor: colors.content.container.primary,
                                borderRadius: radiuses.full
                            }
                        ]}
                    >
                        <PencilIcon
                            color={colors.content.icon.high}
                            size={14}
                        />
                    </View>
                </TouchableOpacity>
            </View>
            <TextInput
                placeholder={localize("superadmin-form-username-placeholder")}
                onChangeText={(text) => handleChange("username", text)}
                title={localize("superadmin-form-username-title")}
                defaultValue={formData.username}
                spreadBehaviour="stretch"
            />
            <TextInput
                placeholder={localize("superadmin-form-email-placeholder")}
                onChangeText={(text) => handleChange("email", text)}
                title={localize("superadmin-form-email-title")}
                defaultValue={formData.email}
                keyboardType="email-address"
                spreadBehaviour="stretch"
                autoCapitalize="none"
            />
            <TextInput
                placeholder={isEdit ? localize("superadmin-form-password-edit-placeholder") : localize("superadmin-form-password-placeholder")}
                onChangeText={(text) => handleChange("password", text)}
                title={localize("superadmin-form-password-title")}
                defaultValue={formData.password}
                spreadBehaviour="stretch"
                secureTextEntry={true}
                variant="hidden"
            />
            {initialData?.role !== "root" && <SelectBox
                initialSelectedItems={organizations.filter(org => formData.organization_ids?.includes(org.id)).map(org => ({
                    ...org,
                    __title: org.name,
                    __key: org.id
                }))}
                onChange={(selectedItems) => {
                    const selectedIds = selectedItems.map(item => item.__key as string);
                    handleChange("organization_ids", selectedIds);
                }}
                placeholder={localize("superadmin-form-org-placeholder")}
                title={localize("superadmin-form-org-title")}
                titleExtractor={(item) => item.name}
                keyExtractor={(item) => item.id}
                selectSheetProps={{
                    portalName: "modal-system"
                }}
                spreadBehaviour="stretch"
                isMultipleSelect={true}
                data={organizations}
                isSearchable={true}
            />}
            <TextInput
                placeholder={localize("superadmin-form-fullname-placeholder")}
                onChangeText={(text) => handleChange("fullName", text)}
                title={localize("superadmin-form-fullname-title")}
                defaultValue={formData.fullName}
                spreadBehaviour="stretch"
            />
            <TextInput
                placeholder={localize("superadmin-form-phone-placeholder")}
                onChangeText={(text) => handleChange("phone", text)}
                title={localize("superadmin-form-phone-title")}
                defaultValue={formData.phone}
                spreadBehaviour="stretch"
            />
            <TextInput
                placeholder={localize("superadmin-form-address-placeholder")}
                onChangeText={(text) => handleChange("address", text)}
                title={localize("superadmin-form-address-title")}
                defaultValue={formData.address}
                spreadBehaviour="stretch"
            />
            <TextInput
                placeholder={localize("superadmin-form-about-placeholder")}
                onChangeText={(text) => handleChange("about", text)}
                title={localize("superadmin-form-about-title")}
                defaultValue={formData.about}
                spreadBehaviour="stretch"
            />
            <TextInput
                placeholder={localize("superadmin-form-admin-note-placeholder")}
                onChangeText={(text) => handleChange("adminNote", text)}
                title={localize("superadmin-form-admin-note-title")}
                defaultValue={formData.adminNote}
                spreadBehaviour="stretch"
            />
        </ScrollView>
        <View
            style={[
                stylesheet.buttonsContainer,
                {
                    flexDirection: isMobile ? "column-reverse" : "row",
                    marginTop: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.buttonWrapper}
            >
                <Button
                    title={localize("superadmin-form-cancel")}
                    onPress={() => NCoreUIKitModal.close({
                        id: modalId
                    })}
                    spreadBehaviour="stretch"
                    type="neutral"
                />
            </View>
            <View
                style={stylesheet.buttonWrapper}
            >
                <Button
                    title={isEdit ? localize("superadmin-form-update") : localize("superadmin-form-save")}
                    spreadBehaviour="stretch"
                    isLoading={isSubmitting}
                    onPress={handleSubmit}
                />
            </View>
        </View>
    </View>;
};
export default SuperAdminFormModal;
